import {
    externalFetch
} from '@js/actions/fetch';

import {pushError} from '@js/actions/errorManagement';

import AnalyticsService from '@js/modules/analyticsService';
import {COORDINATES_PRECISION} from '@js/modules/maps/mapConstants';

import {resolveRoutingProfile} from '@js/components/modules/constants';

const normalizeString = (string) => string ?
    string.normalize('NFD')
        .trim()
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\s-_+@#$%^&*,;]/g, '')
        .toLowerCase()
    : string;

// Cheap check: is the edit distance between two strings <= 1 (one substitution/insertion/deletion)?
// Used to tell an already-good fuzzy match (typo Photon resolved) from a genuinely distant one.
const withinOneEdit = (a, b) => {
    const la = a.length;
    const lb = b.length;

    if (Math.abs(la - lb) > 1) {
        return false;
    }

    let i = 0;
    let j = 0;
    let edits = 0;

    while (i < la && j < lb) {
        if (a[i] === b[j]) {
            i++;
            j++;
        } else {
            if (++edits > 1) {
                return false;
            }
            if (la > lb) {
                i++; // deletion from a
            } else if (lb > la) {
                j++; // insertion into a
            } else {
                i++; // substitution
                j++;
            }
        }
    }

    // A leftover trailing character counts as one more edit.
    return edits + (la - i) + (lb - j) <= 1;
};

// Rough distance in km (equirectangular approximation — good enough for a coarse "same region?" test).
const roughDistanceKm = (lat1, lng1, lat2, lng2) => {
    const dLat = lat1 - lat2;
    const dLng = (lng1 - lng2) * Math.cos(((lat1 + lat2) / 2) * Math.PI / 180);

    return Math.sqrt((dLat * dLat) + (dLng * dLng)) * 111;
};

// A close typo match is only trusted within this radius: a verbatim namesake much further away must
// not mask a nearby place Photon buried (e.g. "Chamonix" in South Africa vs Chamonix-Mont-Blanc).
const LOCAL_MATCH_RADIUS_KM = 1000;

const MapRequest = (function () {
    const mapRequest = {
        // MapPlanner routing url
        mapPlannerDirectionUrl: 'https://routing.map-planner.com/route',

        // MapPlanner map-matching url (GraphHopper /match: recale un tracé main levée sur les routes)
        mapPlannerMatchUrl: 'https://routing.map-planner.com/match',

        // Mapbox routing url
        mapboxDirectionUrl: 'https://api.mapbox.com/directions/v5/mapbox/',

        // Mapbox url
        mapboxStaticMapUrl: 'https://api.mapbox.com/styles/v1/mapbox/outdoors-v11/static',

        // Geocoder URL
        geocoderURL: 'https://geocoder.map-planner.com/'
    };

    const _normalizePlaceType = function (placeType) {
        switch (placeType) {
            case 'country':
                return 'place';
            case 'county':
                return 'place';
            case 'state':
                return 'place';
            case 'province':
                return 'place';
            case 'suburb':
                return 'place';
            case 'city':
                return 'place';
            case 'village':
                return 'place';
            case 'town':
                return 'place';
            case 'hamlet':
                return 'place';
            case 'locality':
                return 'place';
            case 'yes':
                return 'place';

            case 'peak':
                return 'peak';
            case 'volcano':
                return 'peak';

            case 'administrative':
                return 'address';
            case 'neighbourhood':
                return 'address';
            case 'house':
                return 'address';
            case 'apartments':
                return 'address';
            case 'residential':
                return 'address';
            case 'secondary':
                return 'address';
            case 'tertiary':
                return 'address';
            case 'university':
                return 'address';

            case 'station':
                return 'poi';
            case 'bus_stop':
                return 'poi';
            case 'subway_entrance':
                return 'poi';
            case 'orchard':
                return 'poi';
            case 'commercial':
                return 'poi';
            case 'government':
                return 'poi';
            case 'stream':
                return 'poi';
            case 'saddle':
                return 'poi';
            case 'parking':
                return 'poi';
            case 'service':
                return 'poi';
            case 'tree':
                return 'poi';
            case 'shelter':
                return 'poi';
            case 'platform':
                return 'poi';
            case 'viewpoint':
                return 'poi';
            case 'car_rental':
                return 'poi';
            case 'post_office':
                return 'poi';

            // case 'restaurant':
            //     return 'restaurant';

            case 'political':
                return false;

            default:
                return 'other';
        }
    };

    const _buildPlaceName = function (feature, {locationOnly = false} = {}) {
        const placeLocation = [
            feature.city,
            // feature.district || feature.city,
            feature.county || feature.postcode?.split(';')
                ?.first(),
            feature.countrycode === 'FR' ? null : feature.state,
            feature.country
        ].compact();

        if (locationOnly) {
            return placeLocation.join(', ');
        } else {
            let placeName = [feature.housenumber, feature.name || feature.street].compact().join(' ');

            if (placeLocation.length) {
                placeName += ` (${placeLocation.join(', ')})`;
            } else {
                placeName += ` (${feature.country})`;
            }

            return placeName;
        }
    };

    const _parsePhotonResponse = function (features) {
        if (features) {
            // Exclude useless data from photon response
            let filteredFeatures = features
                // .filter((feature) => ['place', 'address', 'peak', 'other'].includes(_normalizePlaceType(feature.properties.osm_value)))
                .filter((feature) => !['natural', 'census'].includes(feature.properties.osm_value));

            if (filteredFeatures.length < 3) {
                filteredFeatures = features;
            }

            return filteredFeatures
                .map((feature, index) => ({
                    type: feature.properties.type,
                    // osmId: feature.properties.osm_id,
                    osmType: feature.properties.osm_value,
                    placeType: _normalizePlaceType(feature.properties.osm_value),
                    name: [feature.properties.housenumber, feature.properties.name || feature.properties.street].compact().join(' '),
                    fullName: _buildPlaceName(feature.properties),
                    placeName: _buildPlaceName(feature.properties, {locationOnly: true}),
                    center: feature.geometry?.coordinates,
                    bbox: feature.properties.extent,
                    country: feature.properties.countrycode,
                    relevance: index
                }));
        } else {
            return [];
        }
    };

    // Languages the geocoder index carries names for (imported with the five site languages); any other
    // locale asks for the local name. An index built before refuses it/es (HTTP 400): that request is sent
    // again with the local names, and the language is dropped for the rest of the session. en/de/fr are in
    // every index: a 400 there is about the request itself, never the language.
    const PHOTON_LANGUAGES = ['en', 'de', 'fr', 'it', 'es'];
    const PHOTON_RECENT_LANGUAGES = ['it', 'es'];
    const _refusedPhotonLanguages = new Set();

    const _photonLanguage = (locale) => (PHOTON_LANGUAGES.includes(locale) && !_refusedPhotonLanguages.has(locale) ? locale : 'default');

    // buildUrl(language) gives the request for a language (null: no lang parameter)
    const _photonFetch = (buildUrl, locale, requestOptions = {}) => {
        const language = locale ? _photonLanguage(locale) : null;

        return externalFetch(buildUrl(language), {}, requestOptions)
            .then((data) => {
                if (data?.status === 400 && PHOTON_RECENT_LANGUAGES.includes(language)) {
                    _refusedPhotonLanguages.add(language);

                    return externalFetch(buildUrl('default'), {}, requestOptions);
                }

                return data;
            });
    };

    const _buildPhotonGeocodingUrl = function (query, country, language = null, proximity = null, limit = 8) {
        // available layers:
        // house, street, locality, district, city, county, state, country, other (e.g. natural features)

        let geocodingUrl = MapRequest.geocoderURL + 'api/';

        geocodingUrl += `?q=${encodeURIComponent(query)}`;

        geocodingUrl += `&limit=${limit}`;

        geocodingUrl += '&layer=' + ['house', 'street', 'locality', 'district', 'city', 'state', 'other'].join('&layer=');

        // "tourism" and "amenity" tags are useful to find parkings.
        //
        // `highway` must NOT be in this list of excluded KEYS: Photon matches osm_tag on the key as
        // well as the value, and every street is `highway=*`. Excluding the key emptied the `street`
        // layer requested just above, so a street address fell back to whatever POI or locality
        // matched the typed words — in another city, often in another country. It stayed invisible
        // here because the French BAN import gives every address its own `place=house` node, so FR
        // queries resolved without ever needing the street.
        geocodingUrl += '&osm_tag=!' + ['company', 'shop', 'emergency', 'healthcare', 'railway', 'office', 'boundary', 'club', 'craft', 'shop', 'landuse', 'unclassified', 'man_made', 'leisure', 'bridge'].join('&osm_tag=!');

        // The highway noise that is not a street, excluded by VALUE so the road itself survives
        // (without this, "Main Street Boston" answers a milestone before the street).
        geocodingUrl += '&osm_tag=!' + ['highway:crossing', 'highway:traffic_signals', 'highway:street_lamp', 'highway:bus_stop', 'highway:milestone', 'highway:motorway_junction', 'highway:turning_circle', 'highway:speed_camera', 'highway:elevator', 'highway:passing_place', 'highway:give_way', 'highway:stop', 'highway:emergency_access_point'].join('&osm_tag=!');

        if (proximity && proximity.lat !== 0 && proximity.lng !== 0) {
            let latitude = parseFloat(proximity.lat);
            let longitude = parseFloat(proximity.lng);

            // Ensure lat or long don't end with 0
            latitude = (latitude + 0.001).toFixed(3);
            longitude = (longitude + 0.001).toFixed(3);

            geocodingUrl += `&lat=${encodeURIComponent(latitude)}`;
            geocodingUrl += `&lon=${encodeURIComponent(longitude)}`;

            // zoom controls the radius of the proximity bias: mid-range values (~10-13) let nearby
            // places outrank globally-prominent far ones, while too low (radius too wide) or too high
            // (radius too tight) both let global OSM importance win again. Callers set proximity.zoom
            // from how precise the location is: tight for the user's real position, broad for the
            // country-center fallback. location_bias_scale is left unset on purpose (default 0.4): 0.2
            // was measured worse (a closer namesake beat the Snowdon summit, an avenue beat Banff).
            geocodingUrl += `&zoom=${proximity.zoom || 7}`;
        }

        if (language) {
            geocodingUrl += `&lang=${language}`;
        }

        return geocodingUrl;
    };

    // Build a minimal GPX 1.1 track from an array of {lat, lng} points.
    // GraphHopper /match expects a GPX body (application/gpx+xml).
    const _buildGpxTrack = function (pointsArray) {
        const precision = COORDINATES_PRECISION;
        const trackPoints = pointsArray
            .map((point) => `<trkpt lat="${point.lat.toFixed(precision)}" lon="${point.lng.toFixed(precision)}"/>`)
            .join('');

        return `<?xml version="1.0" encoding="UTF-8"?><gpx version="1.1" creator="MapPlanner" xmlns="http://www.topografix.com/GPX/1/1"><trk><trkseg>${trackPoints}</trkseg></trk></gpx>`;
    };

    const _mapRouteTypeToMapboxProfile = function (routeType) {
        switch (routeType) {
            case 'car':
            case 'car_fastest':
                return 'driving';
            case 'bike':
            case 'mtb':
                return 'cycling';
            default:
                return 'walking';
        }
    };

    // MapPlanner Graphhopper available route types (no car: driving routes come from Mapbox):
    // - foot_basic: walk, foot without elevation with a walking speed from 2 to 5km/h
    // - running: foot without elevation with a running speed to 9km/h
    // - foot_safe: foot preferring paths over car roads (walk/running opt-in)
    // - hike: shortest hike with elevation
    // - hike_fastest: fastest hike with elevation (hiking isochrone)
    // - hike_safe: hike preferring paths over car roads, forest tracks kept
    // - hike_waymarked: hike restricted to officially waymarked trails (foot_network penalty)
    // - hike_safe_waymarked: hike_safe restricted to waymarked trails (default for hiking)
    // - bike: bike with elevation
    mapRequest.getDirectionsAPIUrl = function (pointsArray, routingService, {
        routeType = 'hike',
        elevation = false,
        // Round-trip generation (GraphHopper only): { distance (m), seed }. When set we send a
        // single start point and let GraphHopper build a loop of the target distance.
        roundTrip = null,
        // Routing safety axes (self-hosted engine only — Mapbox keeps the base profile below):
        // waymarkedOnly = stick to marked trails; preferPaths = prefer paths / avoid car roads.
        waymarkedOnly = false,
        preferPaths = false
    } = {}, requestOptions = {}) {
        let url;

        let i;
        let encodedPoints = '';

        if (routingService === 'map_planner') {
            // A round trip is generated from one origin point (departure = arrival).
            const routePoints = roundTrip ? pointsArray.slice(0, 1) : pointsArray;
            for (i = 0; i < routePoints.length; i++) {
                if (encodedPoints.length > 0) {
                    encodedPoints += '&';
                }
                encodedPoints += 'point=' + encodeURIComponent(routePoints[i].lat.toFixed(COORDINATES_PRECISION)) + ',' + encodeURIComponent(routePoints[i].lng.toFixed(COORDINATES_PRECISION));
            }

            // Map the base profile + safety axes to the dedicated GraphHopper profile (matrix).
            const profile = resolveRoutingProfile(routeType, {safe: preferPaths, waymarked: waymarkedOnly});

            url = `${MapRequest.mapPlannerDirectionUrl}?${encodedPoints}&profile=${profile}&type=json&elevation=${elevation}&instructions=false&points_encoded=true&details=road_class&details=surface`;

            if (roundTrip) {
                // algorithm=round_trip requires flexible mode (ch.disable=true) on the server.
                url += `&algorithm=round_trip&round_trip.distance=${Math.round(roundTrip.distance)}&round_trip.seed=${roundTrip.seed || 0}&ch.disable=true`;
            }
        } else {
            for (i = 0; i < pointsArray.length; i++) {
                if (encodedPoints.length > 0) {
                    encodedPoints += ';';
                }
                encodedPoints += pointsArray[i].lng.toFixed(COORDINATES_PRECISION) + ',' + pointsArray[i].lat.toFixed(COORDINATES_PRECISION);
            }

            const mapboxProfile = _mapRouteTypeToMapboxProfile(routeType);
            url = MapRequest.mapboxDirectionUrl + mapboxProfile + '/' + encodedPoints + '?access_token=' + window.settings.mapbox_key + '&overview=full&geometries=polyline' + (elevation ? '&annotations=duration,distance' : '');
        }

        return externalFetch(url, {}, requestOptions);
    };

    // Map-matching (Footpath-style): snap a freehand-drawn trace onto the road network.
    // Only the self-hosted GraphHopper (map_planner) exposes a /match endpoint; when it fails the
    // caller falls back to routing through sampled waypoints.
    mapRequest.matchTrace = function (pointsArray, {
        routeType = 'hike',
        elevation = false,
        // Standard deviation (m) of the trace points. Higher values tell GraphHopper the input is
        // imprecise (finger drawing), which smooths the match and avoids short back-and-forth detours.
        gpsAccuracy = 50,
        // Safety axes for the freehand match (see getDirectionsAPIUrl): waymarked / prefer-paths.
        waymarkedOnly = false,
        preferPaths = false
    } = {}, {timeout} = {}) {
        const profile = resolveRoutingProfile(routeType, {safe: preferPaths, waymarked: waymarkedOnly});
        const url = `${MapRequest.mapPlannerMatchUrl}?profile=${profile}&type=json&elevation=${elevation}&instructions=false&points_encoded=true&gps_accuracy=${gpsAccuracy}&details=road_class&details=surface`;

        let signal;
        if (timeout && typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
            signal = AbortSignal.timeout(timeout);
        }

        return fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/gpx+xml',
                Accept: 'application/json'
            },
            body: _buildGpxTrack(pointsArray),
            signal
        })
            .then((response) => {
                if (!response.ok) {
                    return response.json()
                        .then((data) => ({errors: data.message || response.statusText, status: response.status}))
                        .catch(() => ({errors: response.statusText, status: response.status}));
                }

                return response.json();
            })
            .catch((error) => {
                if (error.name === 'AbortError' || error.name === 'TimeoutError') {
                    return {abort: true, abortReason: error.name};
                }

                return {errors: error.message};
            });
    };

    // Reverse-geocode a coordinate into the nearest place (Photon /reverse), or null when nothing
    // usable comes back. Best-effort by contract: its only caller pre-fills the ride name, so a
    // failure stays silent (no pushError) and never blocks a save.
    //
    // It reuses the search's PARSER but not its exclusions: _parsePhotonResponse falls back to the
    // unfiltered list below three features, and /reverse returns one — so the natural/census
    // exclusion never applies here and a forest or a river can name the route. Accepted: naming a
    // loop "Autour de la Forêt de Soignes" is a fine title, and dropping the only feature would
    // leave the field empty instead.
    mapRequest.reverseGeocode = function (coordinates, options = {}) {
        if (!coordinates || coordinates.length < 2) {
            return Promise.resolve(null);
        }

        const buildUrl = (language) => `${MapRequest.geocoderURL}reverse?lon=${encodeURIComponent(coordinates[0])}&lat=${encodeURIComponent(coordinates[1])}${language ? `&lang=${language}` : ''}`;

        return _photonFetch(buildUrl, options.locale, options.requestOptions || {})
            .then((data) => {
                if (!data || data.errors) {
                    return null;
                }

                return _parsePhotonResponse(data.features)[0] || null;
            })
            .catch(() => null);
    };

    // The features that can legitimately BE the point of a route, as an explicit key:value
    // allow-list. Keys alone would let urban furniture in — the nearest "water" in central Paris is
    // a fountain basin — and those never name a route. Viewpoints and cave entrances were dropped
    // for the same reason after probing the live geocoder: in a city they resolve to a named sight
    // seen from a bridge, or to an artificial grotto in a park, which nobody calls their run.
    const LANDMARK_OSM_TAGS = [
        'water:lake',
        'water:reservoir',
        'water:pond',
        'natural:peak',
        'natural:saddle',
        'natural:glacier',
        'waterway:waterfall',
        'mountain_pass:yes',
        'tourism:alpine_hut',
        'tourism:wilderness_hut',
        'historic:castle',
        'historic:ruins'
    ];

    // Photon searches around a landmark's indexed point (a lake's is its centre), 1 km by default: wide
    // enough that a reached shore is not left out, the footprint rule (formHelper) deciding.
    const LANDMARK_SEARCH_RADIUS_KM = 3;

    // Landmarks around a coordinate, as an UNRANKED list: Photon orders by distance to the indexed
    // centroid, which buries a lake (centroid in the middle of the water) behind a summit a
    // kilometre away. Ranking is the caller's job and uses the footprint — see
    // formHelper.nearestLandmark.
    //
    // Best-effort by contract, like reverseGeocode: its only caller pre-fills the ride name, so a
    // failure stays silent (no pushError), returns [] and never blocks a save.
    mapRequest.reverseGeocodeLandmark = function (coordinates, options = {}) {
        if (!coordinates || coordinates.length < 2) {
            return Promise.resolve([]);
        }

        const buildUrl = (language) => `${MapRequest.geocoderURL}reverse?lon=${encodeURIComponent(coordinates[0])}&lat=${encodeURIComponent(coordinates[1])}&limit=8&radius=${LANDMARK_SEARCH_RADIUS_KM}`
            + '&osm_tag=' + LANDMARK_OSM_TAGS.join('&osm_tag=')
            + (language ? `&lang=${language}` : '');

        return _photonFetch(buildUrl, options.locale, options.requestOptions || {})
            .then((data) => {
                if (!data || data.errors) {
                    return [];
                }

                return _parsePhotonResponse(data.features);
            })
            .catch(() => []);
    };

    mapRequest.locationSearch = function (query, options = {}, requestOptions = {}) {
        const buildUrl = (language) => _buildPhotonGeocodingUrl(query, options.country, language, options.proximity, options.limit);
        const isFirefox = navigator.userAgent.indexOf('Firefox') !== -1;

        return _photonFetch(buildUrl, options.locale, requestOptions)
            .then(function (data) {
                if (!data || data.errors) {
                    AnalyticsService.trackLocationSearchError(data?.errors);
                }

                if (!data || data.errors || data.abort) {
                    return [];
                }

                let features = _parsePhotonResponse(data.features);

                // Ensure all results are unique according to fullName to avoid duplication in search result
                features = features.uniqBy('fullName');

                if (options.country) {
                    features = data.features.filter((feature) => feature.country.toLowerCase() === options.country.toLowerCase());
                }

                features = features.filter((feature) => !!feature.name);

                const sortedFeatures = features
                    .compact()
                    .slice()
                    .sort((locationA, locationB) => {
                        if (options.sortByMatchingName) {
                            if (normalizeString(locationA.name).includes(normalizeString(query))) {
                                return isFirefox ? -1 : 1;
                            }
                        }

                        if (options.sortByLocaleCountry && options.locale) {
                            if (locationA.country?.toLowerCase() === options.locale.toLowerCase()) {
                                return isFirefox ? -1 : 1;
                            }
                        }

                        if (options.sortByPlaceType) {
                            if (locationA.type === 'county' && locationB.type === 'state') {
                                return isFirefox ? -1 : 1;
                            }
                            if (locationA.type === 'city' && locationB.type === 'county') {
                                return isFirefox ? -1 : 1;
                            }
                            if (locationA.type === 'city' && locationA.osmType === 'village' && locationB.type === 'city' && locationB.osmType === 'town') {
                                return isFirefox ? -1 : 1;
                            }
                            if (locationA.type === 'district' && locationB.type === 'city') {
                                return isFirefox ? -1 : 1;
                            }
                            if (locationA.type === 'locality' && locationB.type === 'district') {
                                return isFirefox ? -1 : 1;
                            }
                        }

                        // Sort by default by relevance
                        if (locationA.relevance < locationB.relevance) {
                            return isFirefox ? -1 : 1;
                        } else if (locationA.relevance > locationB.relevance) {
                            return isFirefox ? 1 : -1;
                        } else {
                            return 0;
                        }
                    });

                // Dictionary-free typo recovery. Photon's native fuzzy already resolves close typos
                // (<= 1-2 edits) on single-token names, so only step in when NO result is within one
                // edit of the typed word — the signature of an intended place that is missing/buried
                // (typically a multi-token name like "Chamonix-Mont-Blanc" drowned under exact
                // single-token junk). Retry once on a shorter prefix that stops before a mid/late-word
                // typo (e.g. "chamonnix" -> "chamon" resolves to Chamonix-Mont-Blanc). We deliberately
                // trust Photon when it has a near match, otherwise we would replace a good result with
                // noise (e.g. "hatteville" already returns Hauteville). Language/country-agnostic.
                // The prefix-truncation retry only makes sense for a single place-name token: a
                // multi-word query (street + city, "rue X", "avenue de Y") would never sit within one
                // edit of a single result name, so it would retry every time and truncating its tail is
                // meaningless. Let Photon's native matching handle those directly.
                const isSingleToken = query.trim().indexOf(' ') === -1;
                const normalizedQuery = normalizeString(query);
                const proximity = options.proximity;
                const hasCloseMatch = !isSingleToken || sortedFeatures.some((location) => {
                    if (!withinOneEdit(normalizeString(location.name).slice(0, normalizedQuery.length), normalizedQuery)) {
                        return false;
                    }

                    // With a known user position, ignore a close match that sits on the other side of
                    // the world (a namesake abroad); without one, trust any close match.
                    if (proximity && proximity.lat && proximity.lng && location.center) {
                        return roughDistanceKm(proximity.lat, proximity.lng, location.center[1], location.center[0]) <= LOCAL_MATCH_RADIUS_KM;
                    }

                    return true;
                });

                if (!options._prefixRetry && !options.country && !hasCloseMatch && query.length >= 7) {
                    return MapRequest.locationSearch(query.slice(0, -3), {...options, _prefixRetry: true}, requestOptions)
                        .then((retryFeatures) => (
                            retryFeatures && retryFeatures.length
                                // Recovered matches first, original results kept as fallback below, capped
                                // to the requested limit so merging both queries never grows the list.
                                ? retryFeatures.concat(sortedFeatures).uniqBy('fullName').slice(0, options.limit || 8)
                                : sortedFeatures
                        ));
                }

                return sortedFeatures;
            })
            .catch((error) => {
                pushError(error);
            });
    };

    return mapRequest;
})();

export default MapRequest;
